'use client';

import type { PropsWithChildren } from 'react';
import type { Collection } from '@/app/actions/collection';
import type { User } from '@/types/user/type';
import type { InteractionType } from '~/model/InteractionType';
import { useTranslations } from 'next-intl';
import { useCallback, useEffect, useState } from 'react';
import { fetchCollection } from '@/app/actions/server';
import { ChidrenFormAddBtn } from '@/app/components/ChidrenFormAddBtn';
import { MainLoader } from '@/app/components/Loader';
import { MainColumn } from '@/app/components/MainColumn';
import { useApiErrorContext, useAuthenticatedContext } from '@/app/components/Provider';
import { BeardmanTalking } from '~/components/Beardman/BeardmanTalking/BeardmanTalking';
import { CompleteProfileBannerForm } from '~/components/commons/CompleteProfileBanner/CompleteProfileBannerForm';
import { NotForInternalUserMessage } from '~/components/Interaction/Screens/Messages';
import { journeyColumnTemplate } from '~/components/Journey/config';
import appConst from '~/const/appConst';
import { useRequestState } from '~/hooks/request/useRequestState';
import { ChildSeletor } from './ChildSelector';
import { ContributionWrapper } from './ContributionWrapper';

export const UserSelector = ({
  children,
  interaction,
  skip = false,
}: PropsWithChildren<{
  interaction: InteractionType;
  skip?: boolean;
}>) => {
  const t = useTranslations('FORM');

  const { user } = useAuthenticatedContext();
  const { setError } = useApiErrorContext();

  const [childList, setChildList] = useState<Collection<User>>();

  const { handleSuccess, handleError, startRequest } = useRequestState();

  const getChildren = useCallback(async () => {
    if (!(interaction.for_children && !skip))
      return;

    startRequest();

    const response = await fetchCollection<User>({
      pathname: 'users/me/children',
      revalidate: 0,
    });

    if (response['hydra:description']) {
      handleError();
      return setError(response);
    }

    setChildList(response);
    handleSuccess();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [interaction.for_children, skip]);

  useEffect(() => {
    getChildren();
  }, [getChildren]);

  if (skip) {
    return <>{children}</>;
  }

  if (interaction.for_children && !childList) {
    return <MainLoader />;
  }

  if (!interaction.for_children) {
    if (interaction.only_for === 2 && user.organization === appConst.organization.decathlon) {
      return <NotForInternalUserMessage />;
    }

    return (
      <ContributionWrapper contributor={user as User} interaction={interaction}>
        {children}
      </ContributionWrapper>
    );
  }

  return (
    <div data-testid="children-form">
      {childList?.['hydra:member']?.length > 0
        ? (
            <ChildSeletor childList={childList}>
              {children}
            </ChildSeletor>
          )
        : (
            <div data-cy="sorry-children">
              <BeardmanTalking
                title={t('CHILDREN_HEADER_2')}
                description={t('CHILDREN_DESCRIPTION')}
              />
              <MainColumn column={journeyColumnTemplate}>
                <ChidrenFormAddBtn />
              </MainColumn>
              <CompleteProfileBannerForm user={null} />
            </div>
          )}
    </div>
  );
};
